import { AgGridReact } from 'ag-grid-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import 'ag-grid-enterprise'

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLocaleText } from '../actions/datagrid'
import { hierarchyQuery } from '../api'
import {
	useGetAllPersons,
	useGetAllTeams,
} from '../api/endpoints/personal-data-rest-controller/personal-data-rest-controller'
import { contextMenu, defaultColDef, gridSettings, sideBar, statusBar } from '../common/grid'
import { TemplateProfileImage } from '../common/icons'
import { PdfReport } from '../common/pdfreport'
import { emptyFormatter, eSalaryImage, localized } from '../common/utils'
import { authSelector } from '../reducers'
import { EmployeeGroupsView } from './employeegroups'
import OrgChart from './hierarchy-chart/FlowChart'
import { TeamManagementView } from './teamsmanagement'
import './hierarchy-chart/FlowChart.css'

export function PersonHierarchy() {
	const { t } = useTranslation()

	const { eSalaryCompanyId } = useSelector(authSelector)

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const gridRef = useRef(null)

	const { data } = useQuery(hierarchyQuery)

	const { data: allPersons } = useGetAllPersons()
	const { data: allTeams } = useGetAllTeams()

	/** teams with member nr sets — drives filter chips and border colors in the org chart */
	const teams = useMemo(() => {
		if (!allTeams) return []
		return allTeams.map((team) => ({
			teamName: team.teamName,
			teamCode: team.teamCode,
			color: team.color,
			memberNrs: new Set(team.teamMembers?.map((m) => m.nr).filter((nr) => nr != null) ?? []),
		}))
	}, [allTeams])

	/** nr -> profession from employmentDetails */
	const workTitleMap = useMemo(() => {
		if (!allPersons) return {}
		return Object.fromEntries(
			allPersons
				.filter((p) => p.nr != null && p.employmentDetails?.profession)
				.map((p) => [p.nr, p.employmentDetails.profession]),
		)
	}, [allPersons])

	const gridComponents = {
		profileImage: (params) => {
			const { value } = params
			if (!params.data?.name) return null
			return (
				<div className="profile-image">
					{value && value.length ? (
						<img
							className="ag-image"
							src={eSalaryImage(eSalaryCompanyId, value)}
							crossOrigin="anonymous"
							alt="profile-image"
						/>
					) : (
						<TemplateProfileImage />
					)}
				</div>
			)
		},
	}

	const colDef = [
		{
			field: 'photo',
			headerName: t('Image', 'Kuva'),
			cellRenderer: 'profileImage',
			filter: false,
			sortable: false,
			suppressSizeToFit: true,
			width: 80,
			valueFormatter: () => emptyFormatter,
		},
		{
			field: 'name',
			headerName: t('Name', 'Nimi'),
		},
		{
			field: 'jobTitle',
		},
		{
			field: 'employmentType',
			valueFormatter: ({ value }) => localized.employmentTypes[value],
		},
	]

	const autoGroupColumnDef = {
		headerName: t('NumberAbbr', 'Nro'),
		filter: 'agNumberColumnFilter',
		cellRendererParams: {
			suppressCount: true,
		},
	}

	const getContextMenuItems = useCallback((params) => contextMenu(t, params), [t])

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}

			<h2 className="viewheading">{t('PersonnelHierarchy', 'Henkilöstöhierarkia')}</h2>

			<Tabs
				defaultActiveKey={'employeegroups'}
				onSelect={(activeKey) => activeKey === 'grid' && gridRef.current.api.sizeColumnsToFit()}
			>
				<Tab eventKey="employeegroups" title={t('EmployeeGroups', 'Työntekijäryhmät')}>
					<EmployeeGroupsView />
				</Tab>

				<Tab eventKey="teams" title={t('Teams', 'Tiimit')}>
					<TeamManagementView />
				</Tab>

				<Tab eventKey="orgchart" title={t('OrgChart')}>
					<div className="fc-orgchart-tab">
						<OrgChart
							photoMap={Object.fromEntries(
								(data?.grid || []).filter((r) => r.hier?.length && r.photo).map((r) => [r.hier.at(-1), r.photo]),
							)}
							eSalaryCompanyId={eSalaryCompanyId}
							workTitleMap={workTitleMap}
							teams={teams}
						/>
					</div>
				</Tab>

				<Tab eventKey="grid" title={t('Grid', 'Taulukko')}>
					<AgGridReact
						className="ag-theme-material styles-ag"
						{...gridSettings}
						ref={gridRef}
						getContextMenuItems={getContextMenuItems}
						components={gridComponents}
						treeData
						getDataPath={(grid) => grid.hier}
						autoGroupColumnDef={autoGroupColumnDef}
						columnDefs={colDef}
						defaultColDef={{ ...defaultColDef, enableCellChangeFlash: true }}
						sideBar={sideBar}
						rowData={data?.grid}
						getMainMenuItems={(params) => params.defaultItems.slice(0, -1)} // remove resetColumns
						statusBar={statusBar}
						getLocaleText={(params) => getLocaleText(t, params)}
						animateRows
						//suppressCellFocus
						suppressColumnVirtualisation // https://github.com/ag-grid/ag-grid/issues/1692
						//onFirstDataRendered={params => params.api.sizeColumnsToFit()} // params.api.autoSizeAllColumns()
						//onGridReady={gridReady}
						//initialState={loadViewState(viewStateKey) || initialState}
						//onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
						debug={import.meta.env.DEV}
					/>
				</Tab>
			</Tabs>
		</>
	)
}
